/**
 * 一般化線形モデル（GLM）の実装
 *
 * IRLS（反復再重み付け最小二乗法）アルゴリズムを使用してGLMを推定する
 */

import type { GLMFamily, GLMLink, GLMParameters, GLMDiagnosticSummary, TrainedModel } from '../../types/model.types';
import type { GLMResult } from '../../types/tabs/glm.types';
import type { DatasetId, ColumnId } from '../../types';
import { createModelId } from '../../types';
import { getLinkFunction } from './link-functions';
import {
  computeLeverage,
  computeCooksDistance,
  computeStandardizedResiduals,
  computeDispersionParameter,
  computePearsonChi2
} from './glm-diagnostics';
import logger from './logger';

/**
 * 分布ファミリーのインターフェース
 */
export interface DistributionFamily {
  /**
   * 分散関数 V(μ)
   * @param mu 平均値
   * @returns 分散
   */
  variance(mu: number): number;

  /**
   * デビアンス成分 d(y, μ)
   * @param y 観測値
   * @param mu 予測平均値
   * @returns デビアンス成分
   */
  devianceComponent(y: number, mu: number): number;

  /**
   * 初期μ値の計算
   * @param y 観測値の配列
   * @returns 初期μ値の配列
   */
  initialMu(y: number[]): number[];

  /**
   * yの値が有効かどうかを検証
   * @param y 観測値
   * @returns 有効ならtrue
   */
  validateY(y: number): boolean;

  /**
   * 対数尤度の計算
   * @param y 観測値の配列
   * @param mu 予測平均値の配列
   * @returns 対数尤度
   */
  logLikelihood(y: number[], mu: number[]): number;
}

/**
 * Gaussian Family: 正規分布
 * V(μ) = 1
 * Canonical link: identity
 */
export const GaussianFamily: DistributionFamily = {
  variance(_mu: number): number {
    return 1.0;
  },

  devianceComponent(y: number, mu: number): number {
    const diff = y - mu;
    return diff * diff;
  },

  initialMu(y: number[]): number[] {
    // 単純にyをそのまま初期値として使用
    return [...y];
  },

  validateY(_y: number): boolean {
    return true; // 全ての実数が有効
  },

  logLikelihood(y: number[], mu: number[]): number {
    // Gaussian分布の対数尤度: -1/2 * Σ(y - μ)² + const
    // constは定数項（-n/2 * log(2πσ²)）だが、AIC計算では無視できる
    let sumSquares = 0;
    for (let i = 0; i < y.length; i++) {
      const diff = y[i] - mu[i];
      sumSquares += diff * diff;
    }
    return -0.5 * sumSquares;
  }
};

/**
 * Binomial Family: 二項分布
 * V(μ) = μ(1-μ)
 * Canonical link: logit
 */
export const BinomialFamily: DistributionFamily = {
  variance(mu: number): number {
    return mu * (1 - mu);
  },

  devianceComponent(y: number, mu: number): number {
    // デビアンス: 2 * [y*log(y/μ) + (1-y)*log((1-y)/(1-μ))]
    const epsilon = 1e-10;
    const muClamped = Math.max(epsilon, Math.min(1 - epsilon, mu));
    const yClamped = Math.max(epsilon, Math.min(1 - epsilon, y));

    let dev = 0;
    if (yClamped > 0) {
      dev += yClamped * Math.log(yClamped / muClamped);
    }
    if (yClamped < 1) {
      dev += (1 - yClamped) * Math.log((1 - yClamped) / (1 - muClamped));
    }
    return 2 * dev;
  },

  initialMu(y: number[]): number[] {
    // 0と1を避けるために調整
    return y.map(yi => {
      if (yi <= 0) return 0.25;
      if (yi >= 1) return 0.75;
      return yi;
    });
  },

  validateY(y: number): boolean {
    return y >= 0 && y <= 1;
  },

  logLikelihood(y: number[], mu: number[]): number {
    // Binomial分布の対数尤度: Σ[y*log(μ) + (1-y)*log(1-μ)]
    let logLik = 0;
    const epsilon = 1e-10;

    for (let i = 0; i < y.length; i++) {
      const yi = y[i];
      const mui = Math.max(epsilon, Math.min(1 - epsilon, mu[i]));

      // y * log(μ)
      if (yi > 0) {
        logLik += yi * Math.log(mui);
      }

      // (1-y) * log(1-μ)
      if (yi < 1) {
        logLik += (1 - yi) * Math.log(1 - mui);
      }
    }

    return logLik;
  }
};

/**
 * Poisson Family: ポアソン分布
 * V(μ) = μ
 * Canonical link: log
 */
export const PoissonFamily: DistributionFamily = {
  variance(mu: number): number {
    return mu;
  },

  devianceComponent(y: number, mu: number): number {
    // デビアンス: 2 * [y*log(y/μ) - (y-μ)]
    const epsilon = 1e-10;
    const muClamped = Math.max(epsilon, mu);

    if (y === 0) {
      return 2 * muClamped;
    }

    return 2 * (y * Math.log(y / muClamped) - (y - muClamped));
  },

  initialMu(y: number[]): number[] {
    // 0を避けるために調整
    return y.map(yi => Math.max(0.1, yi));
  },

  validateY(y: number): boolean {
    return y >= 0 && Number.isInteger(y);
  },

  logLikelihood(y: number[], mu: number[]): number {
    // Poisson分布の対数尤度: Σ(y * log(μ) - μ - log(y!))
    let logLik = 0;
    const epsilon = 1e-10;

    for (let i = 0; i < y.length; i++) {
      const yi = y[i];
      const mui = Math.max(epsilon, mu[i]);

      // y * log(μ)
      logLik += yi * Math.log(mui);

      // -μ
      logLik -= mui;

      // -log(y!)
      for (let j = 1; j <= yi; j++) {
        logLik -= Math.log(j);
      }
    }

    return logLik;
  }
};

/**
 * Gamma Family: ガンマ分布
 * V(μ) = μ²
 * Canonical link: inverse
 */
export const GammaFamily: DistributionFamily = {
  variance(mu: number): number {
    return mu * mu;
  },

  devianceComponent(y: number, mu: number): number {
    // デビアンス: 2 * [(y-μ)/μ - log(y/μ)]
    const epsilon = 1e-10;
    const muClamped = Math.max(epsilon, mu);
    const yClamped = Math.max(epsilon, y);

    return 2 * ((yClamped - muClamped) / muClamped - Math.log(yClamped / muClamped));
  },

  initialMu(y: number[]): number[] {
    // 正の値を保証
    return y.map(yi => Math.max(0.1, yi));
  },

  validateY(y: number): boolean {
    return y > 0;
  },

  logLikelihood(y: number[], mu: number[]): number {
    // Gamma分布の対数尤度（shape parameter α=1、つまり指数分布を仮定）
    // log L = Σ[-log(μ) - y/μ]
    // 注意: GLMのGammaではquasi-likelihoodを使用することが一般的だが、
    // ここでは簡易版として指数分布を仮定
    let logLik = 0;
    const epsilon = 1e-10;

    for (let i = 0; i < y.length; i++) {
      const yi = Math.max(epsilon, y[i]);
      const mui = Math.max(epsilon, mu[i]);

      // -log(μ)
      logLik -= Math.log(mui);

      // -y/μ
      logLik -= yi / mui;
    }

    return logLik;
  }
};

/**
 * Negative Binomial Family: 負の二項分布
 * V(μ) = μ + μ²/θ
 * Canonical link: log
 *
 * θ (theta)は過分散パラメータ：
 * - θ → ∞: Poissonに収束 (分散 = 平均)
 * - θが小さい: 大きな過分散
 */
export class NegativeBinomialFamily implements DistributionFamily {
  constructor(private theta: number) {
    logger.assert(theta > 0, 'Negative Binomial theta must be positive');
  }

  variance(mu: number): number {
    // V(μ) = μ + μ²/θ
    return mu + (mu * mu) / this.theta;
  }

  devianceComponent(y: number, mu: number): number {
    // デビアンス: 2 * [y*log(y/μ) - (y+θ)*log((y+θ)/(μ+θ))]
    const epsilon = 1e-10;
    const muClamped = Math.max(epsilon, mu);
    const theta = this.theta;

    if (y === 0) {
      // 特殊ケース: d(0, μ) = 2θ × log(1 + μ/θ)
      return 2 * theta * Math.log(1 + muClamped / theta);
    }

    // 一般ケース
    const term1 = y * Math.log(y / muClamped);
    const term2 = (y + theta) * Math.log((y + theta) / (muClamped + theta));
    return 2 * (term1 - term2);
  }

  initialMu(y: number[]): number[] {
    // Poissonと同じ: 0を避ける
    return y.map(yi => Math.max(0.1, yi));
  }

  validateY(y: number): boolean {
    // 非負整数（カウントデータ）
    return y >= 0 && Number.isInteger(y);
  }

  /**
   * Negative Binomialの対数尤度を計算
   * log L = Σ [log(Γ(y + θ)) - log(Γ(θ)) - log(y!) + θ*log(θ/(θ+μ)) + y*log(μ/(θ+μ))]
   */
  logLikelihood(y: number[], mu: number[]): number {
    let logLik = 0;
    const theta = this.theta;

    for (let i = 0; i < y.length; i++) {
      const yi = y[i];
      const mui = mu[i];

      // log(Γ(y + θ)) - log(Γ(θ))
      // これはlgamma(y+θ) - lgamma(θ)で計算できますが、
      // JavaScriptにはlgamma関数がないので、Stirlingの近似を使うか、
      // シンプルにΣlog(j)を計算します
      let logGammaTerm = 0;
      for (let j = 0; j < yi; j++) {
        logGammaTerm += Math.log(theta + j);
      }

      // log(y!)
      let logFactorial = 0;
      for (let j = 1; j <= yi; j++) {
        logFactorial += Math.log(j);
      }

      // θ * log(θ/(θ+μ)) + y * log(μ/(θ+μ))
      const prob = theta / (theta + mui);
      const logProb1 = theta * Math.log(prob);
      const logProb2 = yi * Math.log(1 - prob);

      logLik += logGammaTerm - logFactorial + logProb1 + logProb2;
    }

    return logLik;
  }
}

/**
 * 分布ファミリーのレジストリ
 *
 * Note: Negative Binomialはthetaパラメータが必要なため、
 * このレジストリには含めず、fitGLM()内で直接インスタンス化する
 */
export const Families: Record<Exclude<GLMFamily, 'negative-binomial'>, DistributionFamily> = {
  gaussian: GaussianFamily,
  binomial: BinomialFamily,
  poisson: PoissonFamily,
  gamma: GammaFamily
};

/**
 * GLMの進捗情報
 */
export interface GLMProgress {
  /** 進捗の種類 */
  stage: 'irls' | 'theta-estimation';
  /** 現在の反復回数 */
  iteration: number;
  /** 最大反復回数 */
  maxIterations: number;
  /** 追加情報（θ推定時の探索範囲など） */
  details?: string;
  /** 現在のパラメータ推定値（係数） */
  coefficients?: number[];
  /** 現在のdeviance値 */
  deviance?: number;
  /** 現在のθ値（Negative Binomial専用） */
  theta?: number;
  /** θ推定中のIRLSかどうか */
  isDuringThetaEstimation?: boolean;
  /** 黄金分割探索で採用されたθかどうか（最終的に残された値） */
  isAdoptedTheta?: boolean;
  /** 黄金分割探索のイテレーション番号 */
  thetaIteration?: number;
  /** 黄金分割探索での位置（Left, Right, Mid1, Mid2） */
  goldenSectionIndicator?: 'Left' | 'Right' | 'Mid1' | 'Mid2';
  /** 対数尤度（Negative Binomial専用、各θでの最終収束時のみ） */
  logLikelihood?: number;
}

/**
 * GLMのオプション
 */
export interface GLMOptions {
  includeIntercept?: boolean;
  maxIterations?: number;
  tolerance?: number;
  /** 進捗コールバック（オプション） */
  onProgress?: (progress: GLMProgress) => void;
  /** θ推定中のIRLS実行かどうか（内部使用） */
  _isDuringThetaEstimation?: boolean;
  /** θ推定中の現在のθ値（内部使用） */
  _currentTheta?: number;
  /** 黄金分割探索で採用されたθ値のSet（内部使用） */
  _adoptedThetas?: Set<string>;
  /** 黄金分割探索の現在のイテレーション番号（内部使用） */
  _thetaIteration?: number;
  /** 黄金分割探索での現在の位置（内部使用） */
  _goldenSectionIndicator?: 'Left' | 'Right' | 'Mid1' | 'Mid2';
  /** 各θでの対数尤度のMap（内部使用） */
  _thetaLogLikelihoods?: Map<string, number>;
}

/**
 * GLMモデルを推定する
 *
 * @param X 説明変数行列 (n x p)
 * @param y 目的変数ベクトル (n)
 * @param family 分布ファミリー
 * @param link リンク関数
 * @param options オプション設定
 * @param theta Negative Binomial専用の過分散パラメータ (family === 'negative-binomial'の時に必須)
 * @returns GLM推定結果
 */
export function fitGLM(
  X: number[][],
  y: number[],
  family: GLMFamily,
  link: GLMLink,
  options: GLMOptions = {},
  theta?: number
): GLMResult {
  const {
    includeIntercept = true,
    maxIterations = 100,
    tolerance = 1e-8,
    onProgress
  } = options;

  const n = X.length;
  const p = X[0].length;

  logger.info(`[GLM] Starting GLM fitting: family=${family}, link=${link}, n=${n}, p=${p}${theta !== undefined ? `, theta=${theta}` : ''}`);

  // Negative Binomialでthetaが未指定の場合は自動推定
  let thetaEstimated: number | undefined;
  let finalTheta = theta;
  if (family === 'negative-binomial' && theta === undefined) {
    logger.info('[GLM] Theta not specified, estimating from data');
    finalTheta = estimateThetaML(X, y, { includeIntercept, onProgress });
    thetaEstimated = finalTheta;
    logger.info(`[GLM] Estimated theta: ${finalTheta.toFixed(4)}`);
    // 最終IRLSのために_currentThetaを設定
    options._currentTheta = finalTheta;
  }

  // 分布ファミリーとリンク関数を取得
  let familyObj: DistributionFamily;
  if (family === 'negative-binomial') {
    if (finalTheta === undefined || finalTheta <= 0) {
      throw new Error('Theta parameter must be specified and positive for Negative Binomial family');
    }
    familyObj = new NegativeBinomialFamily(finalTheta);
  } else {
    familyObj = Families[family];
  }
  const linkObj = getLinkFunction(link);

  // データ検証
  for (let i = 0; i < n; i++) {
    if (!familyObj.validateY(y[i])) {
      throw new Error(`Invalid y value at index ${i}: ${y[i]} for family ${family}`);
    }
  }

  // デザイン行列の構築（Float64Array、row-major layout）
  const pWithIntercept = includeIntercept ? p + 1 : p;
  const X_design = new Float64Array(n * pWithIntercept);

  for (let i = 0; i < n; i++) {
    if (includeIntercept) {
      X_design[i * pWithIntercept] = 1;
      for (let j = 0; j < p; j++) {
        X_design[i * pWithIntercept + j + 1] = X[i][j];
      }
    } else {
      for (let j = 0; j < p; j++) {
        X_design[i * pWithIntercept + j] = X[i][j];
      }
    }
  }

  // 初期値の設定（Float64Array）
  const mu_init = familyObj.initialMu(y);
  const mu = new Float64Array(mu_init);
  const eta = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    eta[i] = linkObj.link(mu[i]);
  }

  // 係数の初期化（Float64Array）
  // より良い初期値を使用：interceptはyの平均のlink変換値
  const yMean = y.reduce((sum, yi) => sum + yi, 0) / n;
  const muMean = Math.max(0.001, yMean);  // 安全な平均値
  const etaMean = linkObj.link(muMean);

  let beta = new Float64Array(pWithIntercept);
  if (includeIntercept) {
    beta[0] = etaMean;  // interceptにより良い初期値を設定
  }

  let converged = false;
  let iter = 0;

  // IRLSループ
  for (iter = 0; iter < maxIterations; iter++) {
    // 1. 重み行列Wの計算
    // W = (dμ/dη)² / V(μ)
    const W = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const V = familyObj.variance(mu[i]);
      const dmuDeta = linkObj.derivative(eta[i]);

      // 数値的安定性のため、極端に小さい値を避ける
      const epsilon = 1e-10;
      if (V < epsilon || !isFinite(V) || !isFinite(dmuDeta)) {
        W[i] = epsilon;
      } else {
        W[i] = (dmuDeta * dmuDeta) / V;
      }
    }

    // 2. 作業応答変数zの計算
    const z = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const dmuDeta = linkObj.derivative(eta[i]);
      z[i] = eta[i] + (y[i] - mu[i]) / dmuDeta;
    }

    // 3. 重み付き最小二乗法で新しいβを計算（Float64Array版）
    const betaFull = weightedLeastSquares(X_design, n, pWithIntercept, z, W);

    // 4. Step-halving（μの変化を制限してβを計算）
    let stepSize = 1.0;
    const betaNew = new Float64Array(pWithIntercept);
    const maxStepHalving = 20;

    for (let halvingIter = 0; halvingIter < maxStepHalving; halvingIter++) {
      // 試験的なβを計算
      for (let j = 0; j < pWithIntercept; j++) {
        betaNew[j] = beta[j] + stepSize * (betaFull[j] - beta[j]);
      }

      // 試験的なμを計算
      const muTrial: number[] = [];
      let validMu = true;

      for (let i = 0; i < n; i++) {
        let etaTrial = 0;
        for (let j = 0; j < pWithIntercept; j++) {
          etaTrial += X_design[i * pWithIntercept + j] * betaNew[j];
        }

        const muVal = linkObj.inverse(etaTrial);

        // μが有効範囲内かチェック（familyごとに異なる制約）
        let muIsValid = isFinite(muVal);

        if (muIsValid) {
          switch (family) {
            case 'gaussian':
              // Gaussianは任意の実数を取れる
              muIsValid = Math.abs(muVal) < 1e8;
              break;
            case 'binomial':
              // Binomialは[0, 1]の範囲
              muIsValid = muVal > 0 && muVal < 1;
              break;
            case 'poisson':
            case 'negative-binomial':
            case 'gamma':
              // Poisson, Negative Binomial, Gammaは正の値のみ
              muIsValid = muVal > 0 && muVal < 1e8;
              break;
          }
        }

        if (!muIsValid) {
          validMu = false;
          break;
        }
        muTrial.push(muVal);
      }

      if (validMu) {
        // 有効なμが得られたらこのステップサイズを採用
        break;
      }

      // 無効ならstepSizeを半分にする
      stepSize *= 0.5;

      if (stepSize < 1e-10) {
        // ステップサイズが小さすぎたら収束失敗
        logger.warn(`[GLM] Step-halving failed at iteration ${iter + 1}`);
        break;
      }
    }

    // 5. 収束判定
    let maxDiff = 0;
    for (let j = 0; j < pWithIntercept; j++) {
      const diff = Math.abs(betaNew[j] - beta[j]);
      if (diff > maxDiff) maxDiff = diff;
    }

    logger.debug(`[GLM] Iteration ${iter + 1}: maxDiff=${maxDiff}, stepSize=${stepSize.toFixed(4)}, beta=[${Array.from(betaNew).map(b => b.toFixed(4)).join(', ')}]`);

    if (maxDiff < tolerance) {
      converged = true;
      beta = betaNew;
      iter++;

      // 収束時の最終進捗を報告（対数尤度を含む）
      if (onProgress) {
        // μを最終βで再計算
        for (let i = 0; i < n; i++) {
          eta[i] = 0;
          for (let j = 0; j < pWithIntercept; j++) {
            eta[i] += X_design[i * pWithIntercept + j] * beta[j];
          }
          mu[i] = linkObj.inverse(eta[i]);
        }

        // 最終devianceを計算
        let finalDeviance = 0;
        for (let i = 0; i < n; i++) {
          finalDeviance += familyObj.devianceComponent(y[i], mu[i]);
        }

        const currentTheta = options._currentTheta !== undefined ? options._currentTheta : (family === 'negative-binomial' ? finalTheta : undefined);
        const isAdopted = options._adoptedThetas && currentTheta !== undefined
          ? options._adoptedThetas.has(currentTheta.toFixed(6))
          : false;

        // 対数尤度を計算
        let logLikelihood: number | undefined;
        if (family === 'negative-binomial' && currentTheta !== undefined) {
          logLikelihood = familyObj.logLikelihood(y, Array.from(mu));
          if (options._thetaLogLikelihoods) {
            options._thetaLogLikelihoods.set(currentTheta.toFixed(6), logLikelihood);
          }
        }

        onProgress({
          stage: 'irls',
          iteration: iter,
          maxIterations,
          coefficients: [...beta],
          deviance: finalDeviance,
          theta: currentTheta,
          isDuringThetaEstimation: options._isDuringThetaEstimation || false,
          isAdoptedTheta: isAdopted,
          thetaIteration: options._thetaIteration,
          goldenSectionIndicator: options._goldenSectionIndicator,
          logLikelihood
        });
      }

      break;
    }

    // 6. βを更新してη、μを再計算
    beta = betaNew;

    for (let i = 0; i < n; i++) {
      eta[i] = 0;
      for (let j = 0; j < pWithIntercept; j++) {
        eta[i] += X_design[i * pWithIntercept + j] * beta[j];
      }
      mu[i] = linkObj.inverse(eta[i]);

      // μが有効範囲内にあることを確認
      if (!linkObj.validateMu(mu[i]) || !isFinite(mu[i])) {
        // Negative Binomialでは無効な値はエラーとする
        if (family === 'negative-binomial') {
          throw new Error(
            `Negative Binomial GLM: Invalid fitted value at iteration ${iter}: mu[${i}] = ${mu[i]}, eta[${i}] = ${eta[i]}`
          );
        }

        // 他のfamilyは従来通り安全な値に調整
        switch (family) {
          case 'binomial':
            mu[i] = Math.max(0.001, Math.min(0.999, mu[i]));
            break;
          case 'poisson':
          case 'gamma':
            mu[i] = Math.max(0.001, mu[i]);
            break;
          default:
            // Gaussianは制約なし
            break;
        }
        // etaも再計算
        eta[i] = linkObj.link(mu[i]);
      }
    }

    // 発散検出
    if (beta.some(b => Math.abs(b) > 1e10)) {
      throw new Error('GLM fitting diverged: coefficients exceeded 1e10');
    }

    // 進捗通知（現在のβとdevianceを含む）
    if (onProgress) {
      // 現在のdevianceを計算
      let currentDeviance = 0;
      for (let i = 0; i < n; i++) {
        currentDeviance += familyObj.devianceComponent(y[i], mu[i]);
      }

      // 採用されたθかどうかをチェック
      const currentTheta = options._currentTheta !== undefined ? options._currentTheta : (family === 'negative-binomial' ? finalTheta : undefined);
      const isAdopted = options._adoptedThetas && currentTheta !== undefined
        ? options._adoptedThetas.has(currentTheta.toFixed(6))
        : false;

      // 最終イテレーションの場合、対数尤度を計算して記録
      let logLikelihood: number | undefined;
      if (converged || iter + 1 === maxIterations) {
        if (family === 'negative-binomial' && currentTheta !== undefined) {
          // 対数尤度を計算
          logLikelihood = familyObj.logLikelihood(y, Array.from(mu));
          // mapに記録（θ推定中の場合のみ）
          if (options._thetaLogLikelihoods) {
            options._thetaLogLikelihoods.set(currentTheta.toFixed(6), logLikelihood);
          }
        }
      }

      onProgress({
        stage: 'irls',
        iteration: iter + 1,
        maxIterations,
        coefficients: [...beta],
        deviance: currentDeviance,
        theta: currentTheta,
        isDuringThetaEstimation: options._isDuringThetaEstimation || false,
        isAdoptedTheta: isAdopted,
        thetaIteration: options._thetaIteration,
        goldenSectionIndicator: options._goldenSectionIndicator,
        logLikelihood
      });
    }
  }

  if (!converged) {
    logger.warn(`[GLM] Did not converge after ${maxIterations} iterations`);
  }

  // 予測値と残差の計算
  const fittedValues: number[] = [...mu];

  // Negative Binomialでは予測値は常に非負である必要がある
  if (family === 'negative-binomial') {
    logger.assert(
      fittedValues.every(val => val >= 0),
      'Negative Binomial GLM: All fitted values must be non-negative'
    );
  }

  const devianceResiduals: number[] = [];
  const pearsonResiduals: number[] = [];
  const workingResiduals: number[] = [];

  for (let i = 0; i < n; i++) {
    // Deviance residuals
    const devComp = familyObj.devianceComponent(y[i], mu[i]);
    const sign = y[i] >= mu[i] ? 1 : -1;
    devianceResiduals[i] = sign * Math.sqrt(Math.abs(devComp));

    // Pearson residuals
    const V = familyObj.variance(mu[i]);
    pearsonResiduals[i] = (y[i] - mu[i]) / Math.sqrt(V);

    // Working residuals
    workingResiduals[i] = y[i] - mu[i];
  }

  // Devianceの計算
  let deviance = 0;
  for (let i = 0; i < n; i++) {
    deviance += familyObj.devianceComponent(y[i], mu[i]);
  }

  // Null devianceの計算（切片のみのモデル）
  // yMeanは既に上で計算済み
  // 各familyでnull modelの適切なμを計算
  let muNull: number;
  switch (family) {
    case 'binomial':
      // Binomialの場合、yMeanがそのまま確率の推定値
      muNull = Math.max(0.001, Math.min(0.999, yMean));
      break;
    case 'poisson':
    case 'gamma':
    case 'gaussian':
    default:
      muNull = yMean;
      break;
  }

  let nullDeviance = 0;
  for (let i = 0; i < n; i++) {
    nullDeviance += familyObj.devianceComponent(y[i], muNull);
  }

  // 自由度
  const df = n - pWithIntercept;
  const dfNull = n - (includeIntercept ? 1 : 0);

  // AIC, BICの計算
  // 全ての分布でlogLikelihoodメソッドを実装している
  const logLikelihood = familyObj.logLikelihood(y, Array.from(mu));

  // パラメータ数: β の数 + (θ推定時は +1)
  let k = pWithIntercept;
  if (family === 'negative-binomial' && thetaEstimated !== undefined) {
    k += 1;  // θもパラメータとしてカウント
  }

  const aic = 2 * k - 2 * logLikelihood;
  const bic = Math.log(n) * k - 2 * logLikelihood;

  // 標準誤差、z値、p値、信頼区間の計算
  // Fisher情報行列 I = X^T * W * X を計算し、その逆行列から分散共分散行列を得る

  // 最終的な重み行列Wを計算
  const W_final: number[] = [];
  for (let i = 0; i < n; i++) {
    const V = familyObj.variance(mu[i]);
    const dmuDeta = linkObj.derivative(eta[i]);
    const epsilon = 1e-10;
    if (V < epsilon || !isFinite(V) || !isFinite(dmuDeta)) {
      W_final[i] = epsilon;
    } else {
      W_final[i] = (dmuDeta * dmuDeta) / V;
    }
  }

  // Fisher情報行列の計算: I = X^T * W * X
  const fisherInfo: number[][] = [];
  for (let i = 0; i < pWithIntercept; i++) {
    fisherInfo[i] = [];
    for (let j = 0; j < pWithIntercept; j++) {
      let sum = 0;
      for (let k = 0; k < n; k++) {
        sum += X_design[k * pWithIntercept + i] * W_final[k] * X_design[k * pWithIntercept + j];
      }
      fisherInfo[i][j] = sum;
    }
  }

  // 分散共分散行列 = Fisher情報行列の逆行列
  let covarianceMatrix: number[][];
  try {
    covarianceMatrix = invertMatrix(fisherInfo);
  } catch {
    logger.warn('[GLM] Failed to compute covariance matrix, using zeros for standard errors');
    // 逆行列計算に失敗した場合は0で埋める
    const standardErrors = new Array(pWithIntercept).fill(0);
    const zValues = new Array(pWithIntercept).fill(0);
    const pValues = new Array(pWithIntercept).fill(0);
    const confidenceIntervals = Array.from(beta).map(() => ({ lower: 0, upper: 0 }));
    covarianceMatrix = Array(pWithIntercept).fill(0).map(() => new Array(pWithIntercept).fill(0));

    // 診断統計量（leverageは計算できないので0で埋める）
    const leverage = new Array(n).fill(0);
    const dispersionParameter = computeDispersionParameter(deviance, df);
    const standardizedResiduals = new Array(n).fill(0);
    const cooksDistance = new Array(n).fill(0);
    const pearsonChi2 = computePearsonChi2(pearsonResiduals);

    logger.info(`[GLM] Fitting completed: converged=${converged}, iterations=${iter}, deviance=${deviance.toFixed(4)}`);

    const result: GLMResult = {
      coefficients: Array.from(beta),
      standardErrors,
      zValues,
      pValues,
      confidenceIntervals,
      fittedValues,
      residuals: {
        deviance: devianceResiduals,
        pearson: pearsonResiduals,
        working: workingResiduals
      },
      deviance,
      nullDeviance,
      aic,
      bic,
      iterations: iter,
      converged,
      theta: family === 'negative-binomial' ? theta : undefined,
      diagnostics: {
        nObservations: n,
        nIncomplete: 0,  // GLMTabContentで設定される
        degreesOfFreedom: df,
        incompleteObservations: [],  // GLMTabContentで設定される
        fittedValues,
        devianceResiduals,
        pearsonResiduals,
        standardizedResiduals,
        leverage,
        cooksDistance,
        workingWeights: W_final,
        dispersionParameter,
        pearsonChi2
      },
      covarianceMatrix,
      degreesOfFreedom: df,
      nullDegreesOfFreedom: dfNull
    };

    return result;
  }

  // 分散パラメータの計算
  const pearsonChi2 = computePearsonChi2(pearsonResiduals);
  let dispersionParameter: number;
  let dispersionForSE: number; // 標準誤差計算用の分散パラメータ

  if (family === 'negative-binomial') {
    // Negative Binomialの場合（Rと同じ挙動）:
    // - θ推定時 (glm.nb): dispersion = 1（過分散は既にθでモデル化）
    // - θ固定時 (glm with fixed theta): dispersion = Pearson χ² / df
    if (thetaEstimated !== undefined) {
      dispersionParameter = 1.0;
      dispersionForSE = 1.0;
    } else {
      dispersionParameter = pearsonChi2 / df;
      dispersionForSE = pearsonChi2 / df;
    }
  } else if (family === 'poisson' || family === 'binomial') {
    // Poisson/Binomialの場合: dispersion = 1（理論値）
    // 診断用にdeviance-basedも計算
    dispersionParameter = computeDispersionParameter(deviance, df);
    dispersionForSE = 1.0;  // 標準誤差には1を使用
  } else {
    // Gaussian/Gammaの場合: dispersion = deviance / df
    dispersionParameter = computeDispersionParameter(deviance, df);
    dispersionForSE = dispersionParameter;
  }

  // 標準誤差の計算
  // 標準誤差 = sqrt(φ × diag((X'WX)^-1))
  // 分布ごとのφの扱い:
  //   - Poisson/Binomial: φ=1（理論値）
  //   - Gaussian/Gamma: φ=deviance/df（推定）
  //   - Negative Binomial (auto θ): φ=1
  //   - Negative Binomial (fixed θ): φ=Pearson χ²/df
  const standardErrors: number[] = [];
  for (let i = 0; i < pWithIntercept; i++) {
    standardErrors[i] = Math.sqrt(Math.abs(dispersionForSE * covarianceMatrix[i][i]));
  }

  // z値 = 係数 / 標準誤差
  const zValues: number[] = [];
  for (let i = 0; i < pWithIntercept; i++) {
    if (standardErrors[i] > 0) {
      zValues[i] = beta[i] / standardErrors[i];
    } else {
      zValues[i] = 0;
    }
  }

  // p値 = 2 * (1 - Φ(|z|)) （両側検定）
  const pValues: number[] = [];
  for (let i = 0; i < pWithIntercept; i++) {
    pValues[i] = 2 * (1 - standardNormalCDF(Math.abs(zValues[i])));
  }

  // 95%信頼区間 = 係数 ± 1.96 * 標準誤差
  const confidenceIntervals: Array<{ lower: number; upper: number }> = [];
  for (let i = 0; i < pWithIntercept; i++) {
    const margin = 1.96 * standardErrors[i];
    confidenceIntervals[i] = {
      lower: beta[i] - margin,
      upper: beta[i] + margin
    };
  }

  // 診断統計量の計算
  logger.debug('[GLM] Computing diagnostic statistics');

  // Leverage (hat values)
  const leverage = computeLeverage(X_design, W_final, n, pWithIntercept);

  // Standardized residuals (devianceベース)
  const standardizedResiduals = computeStandardizedResiduals(
    devianceResiduals,
    leverage,
    dispersionParameter
  );

  // Cook's distance
  const cooksDistance = computeCooksDistance(
    standardizedResiduals,
    leverage,
    pWithIntercept
  );

  logger.info(`[GLM] Fitting completed: converged=${converged}, iterations=${iter}, deviance=${deviance.toFixed(4)}`);

  const result: GLMResult = {
    coefficients: Array.from(beta),
    standardErrors,
    zValues,
    pValues,
    confidenceIntervals,

    fittedValues,
    residuals: {
      deviance: devianceResiduals,
      pearson: pearsonResiduals,
      working: workingResiduals
    },

    deviance,
    nullDeviance,
    aic,
    bic,

    iterations: iter,
    converged,

    theta: family === 'negative-binomial' ? finalTheta : undefined,
    thetaEstimated: family === 'negative-binomial' ? thetaEstimated : undefined,

    diagnostics: {
      nObservations: n,
      nIncomplete: 0,  // GLMTabContentで設定される
      degreesOfFreedom: df,
      incompleteObservations: [],  // GLMTabContentで設定される
      fittedValues,
      devianceResiduals,
      pearsonResiduals,
      standardizedResiduals,
      leverage,
      cooksDistance,
      workingWeights: W_final,
      dispersionParameter,
      pearsonChi2
    },
    covarianceMatrix,
    degreesOfFreedom: df,
    nullDegreesOfFreedom: dfNull
  };

  return result;
}

/**
 * 重み付き最小二乗法（WLS）
 *
 * @param X デザイン行列 (n x p)
 * @param y 応答変数 (n)
 * @param W 重み (n)
 * @returns 係数ベクトル (p)
 */
function weightedLeastSquares(
  X: Float64Array,
  n: number,
  p: number,
  y: Float64Array,
  W: Float64Array
): Float64Array {
  // X'WX を計算 (p x p 対称行列、row-major)
  const XTX = new Float64Array(p * p);

  for (let i = 0; i < p; i++) {
    for (let j = i; j < p; j++) {  // 対称性を利用
      let sum = 0;
      for (let k = 0; k < n; k++) {
        const xi = X[k * p + i];
        const xj = X[k * p + j];
        sum += W[k] * xi * xj;
      }
      XTX[i * p + j] = sum;
      if (i !== j) {
        XTX[j * p + i] = sum;  // 対称性
      }
    }
  }

  // X'Wy を計算 (p)
  const XTy = new Float64Array(p);
  for (let i = 0; i < p; i++) {
    let sum = 0;
    for (let k = 0; k < n; k++) {
      sum += W[k] * X[k * p + i] * y[k];
    }
    XTy[i] = sum;
  }

  // Cholesky分解で解く
  return solveByCholesky(XTX, XTy, p);
}

/**
 * Cholesky分解を使って線形方程式を解く
 *
 * @param A 正定値対称行列 (n x n) - row-major形式のFlat配列
 * @param b 右辺ベクトル (n)
 * @param n 行列サイズ
 * @returns 解ベクトル (n)
 */
function solveByCholesky(A: Float64Array, b: Float64Array, n: number): Float64Array {
  // Cholesky分解: A = L * L^T (row-major)
  const L = new Float64Array(n * n);

  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= i; j++) {
      let sum = 0;
      for (let k = 0; k < j; k++) {
        sum += L[i * n + k] * L[j * n + k];
      }

      if (i === j) {
        L[i * n + j] = Math.sqrt(A[i * n + i] - sum);
      } else {
        L[i * n + j] = (A[i * n + j] - sum) / L[j * n + j];
      }
    }
  }

  // Forward substitution: L * y = b
  const y = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    let sum = 0;
    for (let j = 0; j < i; j++) {
      sum += L[i * n + j] * y[j];
    }
    y[i] = (b[i] - sum) / L[i * n + i];
  }

  // Back substitution: L^T * x = y
  const x = new Float64Array(n);
  for (let i = n - 1; i >= 0; i--) {
    let sum = 0;
    for (let j = i + 1; j < n; j++) {
      sum += L[j * n + i] * x[j];
    }
    x[i] = (y[i] - sum) / L[i * n + i];
  }

  return x;
}

/**
 * 行列の逆行列を計算（Gauss-Jordan法）
 *
 * @param A 正方行列
 * @returns Aの逆行列
 * @throws Error 特異行列の場合
 */
export function invertMatrix(A: number[][]): number[][] {
  const n = A.length;

  // 拡大行列を作成 [A | I]
  const augmented: number[][] = [];
  for (let i = 0; i < n; i++) {
    augmented[i] = [...A[i]];
    for (let j = 0; j < n; j++) {
      augmented[i].push(i === j ? 1 : 0);
    }
  }

  // Gauss-Jordan消去法
  for (let i = 0; i < n; i++) {
    // ピボット選択
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
        maxRow = k;
      }
    }

    // 行の交換
    [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];

    // ピボットが0に近い場合は特異行列
    if (Math.abs(augmented[i][i]) < 1e-10) {
      throw new Error('Matrix is singular or near-singular');
    }

    // ピボット行を正規化
    const pivot = augmented[i][i];
    for (let j = 0; j < 2 * n; j++) {
      augmented[i][j] /= pivot;
    }

    // 他の行から引く
    for (let k = 0; k < n; k++) {
      if (k !== i) {
        const factor = augmented[k][i];
        for (let j = 0; j < 2 * n; j++) {
          augmented[k][j] -= factor * augmented[i][j];
        }
      }
    }
  }

  // 右半分が逆行列
  const inverse: number[][] = [];
  for (let i = 0; i < n; i++) {
    inverse[i] = augmented[i].slice(n);
  }

  return inverse;
}

/**
 * 標準正規分布の累積分布関数（CDF）
 *
 * @param z z値
 * @returns P(Z <= z)
 */
export function standardNormalCDF(z: number): number {
  // 誤差関数を使用した近似
  // Φ(z) = 0.5 * (1 + erf(z / sqrt(2)))

  // erfの近似（Abramowitz and Stegun, 1964）
  const erf = (x: number): number => {
    const sign = x >= 0 ? 1 : -1;
    x = Math.abs(x);

    const a1 =  0.254829592;
    const a2 = -0.284496736;
    const a3 =  1.421413741;
    const a4 = -1.453152027;
    const a5 =  1.061405429;
    const p  =  0.3275911;

    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
  };

  return 0.5 * (1 + erf(z / Math.sqrt(2)));
}

/**
 * Golden section search for 1D optimization
 * 黄金比探索法による1次元最適化
 *
 * @param f 目的関数（最小化）- (theta, indicator, iteration) => value
 * @param a 探索区間の下限
 * @param b 探索区間の上限
 * @param tol 許容誤差
 * @param onProgress 進捗コールバック（オプション）
 * @param onThetaAdopted θ値が採用されたときのコールバック（オプション）
 * @returns 最小値を与えるx
 */
function goldenSectionSearch(
  f: (x: number, indicator: 'Left' | 'Right' | 'Mid1' | 'Mid2', iteration: number) => number,
  a: number,
  b: number,
  tol: number = 1e-5,
  onProgress?: (iteration: number, maxIterations: number) => void,
  onThetaAdopted?: (theta: number, deviance: number) => void
): number {
  const phi = (1 + Math.sqrt(5)) / 2; // 黄金比
  const resphi = 2 - phi;

  let x1 = a + resphi * (b - a);
  let x2 = b - resphi * (b - a);

  // 初期の2点を採用θとして報告（評価前に）
  if (onThetaAdopted) {
    onThetaAdopted(x1, 0);
    onThetaAdopted(x2, 0);
  }

  // 初期の2点を評価（iteration 0）
  let f1 = f(x1, 'Mid1', 0);
  let f2 = f(x2, 'Mid2', 0);

  let iter = 0;
  const maxIter = 200;  // 広い探索範囲に対応するため増やす

  // 相対誤差と絶対誤差の両方でチェック
  while (iter < maxIter) {
    // 進捗通知
    if (onProgress) {
      onProgress(iter + 1, maxIter);
    }

    const width = Math.abs(b - a);
    const midpoint = (a + b) / 2;
    const relativeTol = Math.abs(width / Math.max(1.0, midpoint));

    // 相対誤差 < tol または 絶対誤差 < tol で収束
    if (relativeTol < tol || width < tol) {
      break;
    }

    iter++;

    if (f1 < f2) {
      // [a, x2]の区間を採用
      b = x2;
      x2 = x1;
      f2 = f1;
      x1 = a + resphi * (b - a);
      // 新しいx1が採用された（評価前に報告）
      if (onThetaAdopted) {
        onThetaAdopted(x1, 0);
      }
      f1 = f(x1, 'Mid1', iter);
    } else {
      // [x1, b]の区間を採用
      a = x1;
      x1 = x2;
      f1 = f2;
      x2 = b - resphi * (b - a);
      // 新しいx2が採用された（評価前に報告）
      if (onThetaAdopted) {
        onThetaAdopted(x2, 0);
      }
      f2 = f(x2, 'Mid2', iter);
    }
  }

  return (a + b) / 2;
}

/**
 * Estimate initial theta using moment method
 * モーメント法によるθの初期値推定
 *
 * @param X デザイン行列
 * @param y 応答変数
 * @returns 初期θ値
 */
function estimateThetaInitial(X: number[][], y: number[]): number {
  // Poissonでfittingして残差から推定
  try {
    const poissonResult = fitGLM(X, y, 'poisson', 'log', { includeIntercept: true });

    // Pearson chi-squareから推定
    const n = y.length;
    const p = poissonResult.coefficients.length;
    const df = n - p;

    // Pearson chi-square / df が過分散の推定値
    const pearsonChi2 = computePearsonChi2(poissonResult.residuals.pearson);
    const dispersion = pearsonChi2 / df;

    // Negative Binomialの分散: Var(Y) = μ + μ²/θ
    // 過分散 φ = Var(Y)/μ = 1 + μ/θ
    // したがって θ ≈ μ̄ / (φ - 1)
    const meanY = y.reduce((sum, yi) => sum + yi, 0) / n;
    const thetaEst = meanY / Math.max(0.01, dispersion - 1);

    // 範囲を制限（下限のみ。上限は設定しない - 大きなθはPoissonに近づくだけ）
    return Math.max(0.1, thetaEst);
  } catch (error) {
    // Poissonでのfittingが失敗した場合はデフォルト値
    logger.warn('[GLM] Failed to estimate initial theta, using default value 1.0', { error });
    return 1.0;
  }
}

/**
 * Profile log-likelihood for Negative Binomial
 * Negative BinomialのProfile log-likelihood
 *
 * @param theta θパラメータ（固定）
 * @param X デザイン行列
 * @param y 応答変数
 * @param options fitGLMオプション
 * @returns 負のlog-likelihood（最小化用）
 */
function profileLogLikelihood(
  theta: number,
  X: number[][],
  y: number[],
  options: {
    includeIntercept: boolean;
    onProgress?: (progress: GLMProgress) => void;
    _adoptedThetas?: Set<string>;
    _thetaIteration?: number;
    _goldenSectionIndicator?: 'Left' | 'Right' | 'Mid1' | 'Mid2';
    _thetaLogLikelihoods?: Map<string, number>;
  }
): number {
  try {
    // θを固定してIRLSでβを推定
    const result = fitGLM(X, y, 'negative-binomial', 'log', {
      ...options,
      _isDuringThetaEstimation: true,
      _currentTheta: theta,
      _adoptedThetas: options._adoptedThetas,
      _thetaIteration: options._thetaIteration,
      _goldenSectionIndicator: options._goldenSectionIndicator,
      _thetaLogLikelihoods: options._thetaLogLikelihoods
    }, theta);

    // 対数尤度を計算（fitGLM内で既にmapに記録済み）
    const familyObj = new NegativeBinomialFamily(theta);
    const logLik = familyObj.logLikelihood(y, result.fittedValues);

    // 負の値を返す（最小化問題に変換）
    return -logLik;
  } catch (error) {
    // エラーの場合は非常に大きな値を返す
    logger.warn(`[GLM] Failed to compute profile log-likelihood for theta=${theta}`, { error });
    return 1e10;
  }
}

/**
 * Estimate theta using profile likelihood maximization
 * Profile likelihoodの最大化によるθの推定
 *
 * @param X デザイン行列
 * @param y 応答変数
 * @param options fitGLMオプション
 * @param onProgress 進捗コールバック（オプション）
 * @returns 推定されたθ値
 */
export function estimateThetaML(
  X: number[][],
  y: number[],
  options: { includeIntercept: boolean; onProgress?: (progress: GLMProgress) => void }
): number {
  // θの初期値を推定
  const thetaInit = estimateThetaInitial(X, y);

  logger.info(`[GLM] Estimating theta using profile likelihood, initial value: ${thetaInit.toFixed(4)}`);

  // 探索区間: 広い範囲を設定
  // 下限: thetaInitの1/10 or 0.01の大きい方
  // 上限: 10^7（これ以上はPoissonとほぼ同じ）
  // thetaInitが不正確な場合でも広範囲を探索できるようにする
  const lowerBound = Math.max(0.01, thetaInit / 10);
  const upperBound = 1e7;  // 固定上限

  // 採用されたθ値を追跡するSet
  const adoptedThetas = new Set<string>();

  // 各θでの対数尤度を記録（最終イテレーションで進捗報告に含めるため）
  const thetaLogLikelihoods = new Map<string, number>();

  // Profile log-likelihoodを最小化（= log-likelihoodを最大化）
  const objectiveFnWithAdopted = (theta: number, indicator: 'Left' | 'Right' | 'Mid1' | 'Mid2', iteration: number) => {
    const negLogLik = profileLogLikelihood(theta, X, y, {
      ...options,
      _adoptedThetas: adoptedThetas,
      _thetaIteration: iteration,
      _goldenSectionIndicator: indicator,
      _thetaLogLikelihoods: thetaLogLikelihoods
    });

    // 対数尤度はfitGLM内で既にmapに記録済み

    return negLogLik;
  };

  const thetaML = goldenSectionSearch(
    objectiveFnWithAdopted,
    lowerBound,
    upperBound,
    1e-5,
    options.onProgress ? (iter, maxIter) => {
      options.onProgress!({
        stage: 'theta-estimation',
        iteration: iter,
        maxIterations: maxIter,
        details: `Searching θ in [${lowerBound.toFixed(2)}, ${upperBound.toFixed(0)}]`
      });
    } : undefined,
    options.onProgress ? (theta: number) => {
      // 採用されたθ値を記録
      adoptedThetas.add(theta.toFixed(6));
    } : undefined
  );

  logger.info(`[GLM] Theta estimation converged: ${thetaML.toFixed(4)}`);

  return thetaML;
}

/**
 * GLMResultをTrainedModelに変換する
 *
 * 以前はGLMResultのtoModelメソッドとして実装されていたが、
 * Web Worker対応のためにスタンドアロン関数に変更。
 *
 * @param result GLM計算結果
 * @param name モデル名
 * @param datasetId 学習データセットID
 * @param features 説明変数のカラムID
 * @param target 目的変数のカラムID
 * @param family 分布ファミリー
 * @param link リンク関数
 * @param includeIntercept 切片を含むか
 * @param maxIterations 最大反復回数
 * @param tolerance 収束判定の許容誤差
 * @param diagnosticDatasetId 診断データセットのID
 * @returns 学習済みモデル
 */
export function glmResultToModel(
  result: GLMResult,
  name: string,
  datasetId: DatasetId,
  features: ColumnId[],
  target: ColumnId,
  family: GLMFamily,
  link: GLMLink,
  includeIntercept: boolean,
  maxIterations: number,
  tolerance: number,
  diagnosticDatasetId: DatasetId
): TrainedModel {
  const id = createModelId(crypto.randomUUID());

  const featureNames = features.map(id => String(id));
  const targetName = String(target);

  const metadata: TrainedModel['metadata'] = {
    createdAt: new Date().toISOString(),
    trainingDatasetId: datasetId,
    features: featureNames,
    target: targetName,
    sampleSize: result.diagnostics.nObservations
  };

  const diagnosticSummary: GLMDiagnosticSummary = {
    nObservations: result.diagnostics.nObservations,
    nIncomplete: result.diagnostics.nIncomplete,
    degreesOfFreedom: result.degreesOfFreedom,
    incompleteObservations: result.diagnostics.incompleteObservations,
    dispersionParameter: result.diagnostics.dispersionParameter,
    pearsonChi2: result.diagnostics.pearsonChi2,
    diagnosticDatasetId
  };

  const parameters: GLMParameters = {
    coefficients: result.coefficients,
    intercept: includeIntercept ? result.coefficients[0] : undefined,
    standardErrors: result.standardErrors,
    zValues: result.zValues,
    pValues: result.pValues,
    confidenceIntervals: result.confidenceIntervals,
    family,
    link,
    theta: result.theta,
    deviance: result.deviance,
    nullDeviance: result.nullDeviance,
    degreesOfFreedom: result.degreesOfFreedom,
    nullDegreesOfFreedom: result.nullDegreesOfFreedom,
    aic: result.aic,
    bic: result.bic,
    iterations: result.iterations,
    converged: result.converged,
    covarianceMatrix: result.covarianceMatrix,
    diagnosticSummary
  };

  const hyperparameters = {
    family,
    link,
    includeIntercept,
    maxIterations,
    tolerance,
    ...(family === 'negative-binomial' && result.thetaEstimated !== undefined
      ? { thetaEstimated: result.thetaEstimated }
      : {})
  };

  return {
    id,
    type: 'glm',
    name,
    metadata,
    parameters,
    hyperparameters,
    predictorType: 'numeric'
  };
}
